@if (pageLoading) {
  <app-loading-overlay [visible]="true" [indeterminate]="true"></app-loading-overlay>
} @else if (currentFund) {
  <form [formGroup]="grantForm">
    <!-- ================================================================ -->
    <!-- SECTION 1: CHARITY SELECTION -->
    <!-- ================================================================ -->
    <div class="form-section">
      <div class="section-header">
        <span class="section-number">1</span>
        <span class="section-title">
          {{
            (currentFund.fundType === fundTypes.CHARITY
              ? 'distribution.form.charity.my_charity'
              : 'grant.form.charity.choose'
            ) | translateLang
          }}
        </span>
      </div>

      <div class="field-group">
        <div class="field-label-row">
          <label class="field-label">
            {{
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.form.charity_or_fund.field_label'
                : 'grant.form.charity_or_fund.field_label'
              ) | translateLang
            }}<span class="required-asterisk">*</span>
          </label>
          @if (currentFund.fundType !== fundTypes.CHARITY) {
            <span class="required-hint">{{ 'grant.form.required_field' | translateLang }}</span>
          }
        </div>
        <div class="autocomplete-wrapper" [class.is-disabled]="receiverFundNameAndNumber.disabled">
          <p-autocomplete
            formControlName="receiverFundName"
            [suggestions]="charities"
            field="displayName"
            [forceSelection]="true"
            [dropdown]="false"
            [minLength]="0"
            [completeOnFocus]="true"
            [showClear]="true"
            [disabled]="receiverFundNameAndNumber.disabled"
            [placeholder]="
              currentFund.fundType !== fundTypes.CHARITY
                ? ('grant.form.charity.placeholder' | translateLang)
                : ''
            "
            styleClass="w-full"
            (completeMethod)="searchCharities($event)"
            (onSelect)="onSelectCharity($event)"
            (onClear)="onClearCharity()"
          >
            <ng-template pTemplate="clearicon">
              <app-icon name="close" [size]="6" color="white"></app-icon>
            </ng-template>
            <ng-template pTemplate="item" let-charity>
              <div class="charity-item">
                <span class="charity-name">{{ charity.fundName }}</span>
                <span class="charity-number">#{{ charity.fundNumberDisplay || charity.fundNumber }}</span>
              </div>
            </ng-template>
          </p-autocomplete>
          <div class="disabled-text-display">
            {{ receiverFundNameAndNumber.value?.displayName }}
          </div>
        </div>

        @if (
          receiverFundNameAndNumber.invalid &&
          (receiverFundNameAndNumber.dirty || receiverFundNameAndNumber.touched) &&
          currentFund.fundType !== fundTypes.CHARITY
        ) {
          <small class="error-text">{{
            'grant.form.charity.error_required' | translateLang
          }}</small>
        }

        @if (!receiverFundNameAndNumber.value && currentFund.fundType !== fundTypes.CHARITY) {
          <i class="helper-text" [innerHTML]="'grant.form.charity.not_found' | translateLang"></i>
        }

        @if (!loadingProjects && charityProjects.length > 0 && !shouldHideProjectField) {
          <div class="field-group mt-3">
            <label class="field-label">{{ 'grant.form.project.label' | translateLang }}</label>
            <i class="helper-text">{{ 'grant.form.project.description' | translateLang }}</i>
            <div class="autocomplete-wrapper" [class.is-disabled]="charityProjectControl?.disabled">
              <p-autocomplete
                formControlName="charityProject"
                [suggestions]="projectSuggestions"
                field="displayName"
                [forceSelection]="true"
                [dropdown]="false"
                [minLength]="0"
                [completeOnFocus]="true"
                [showClear]="true"
                [placeholder]="'grant.form.project.placeholder' | translateLang"
                styleClass="w-full"
                (completeMethod)="searchProjects($event)"
                (onSelect)="emitChanges()"
                (onClear)="onClearProject()"
              >
                <ng-template pTemplate="clearicon">
                  <app-icon name="close" [size]="6" color="white"></app-icon>
                </ng-template>
                <ng-template pTemplate="item" let-project>
                  <div class="charity-item">
                    <span class="charity-name">{{ project.entityName }}</span>
                    <span class="charity-number">#{{ project.fundNumberDisplay || project.fundNumber }}</span>
                  </div>
                </ng-template>
              </p-autocomplete>
              <div class="disabled-text-display">
                {{ charityProjectControl?.value?.displayName }}
              </div>
            </div>
          </div>
        }

        @if (
          receiverFundNameAndNumber.value &&
          isReceiverEqualWithSenderFund() &&
          selectedCharityPurpose
        ) {
          <div class="info-badge">
            <span class="info-badge__label"
              >{{
                (currentFund.fundType === fundTypes.CHARITY
                  ? 'distribution.form.charity.purpose'
                  : 'grant.form.charity.purpose'
                ) | translateLang
              }}:</span
            >
            <span class="info-badge__value">{{ selectedCharityPurpose }}</span>
          </div>
        }
      </div>
    </div>

    <hr class="section-divider" />

    <!-- ================================================================ -->
    <!-- SECTION 2: DETAILS (Amount + Timing) -->
    <!-- ================================================================ -->
    <div class="form-section">
      <div class="section-header">
        <span class="section-number">2</span>
        <span class="section-title">{{
          (currentFund.fundType === fundTypes.CHARITY
            ? 'distribution.form.details.title'
            : 'grant.form.details.title'
          ) | translateLang
        }}</span>
      </div>

      <!-- Amount -->
      <div class="field-group">
        <div class="field-label-row">
          <label class="field-label">
            {{ 'contribute.section4.amount.label' | translateLang
            }}<span class="required-asterisk">*</span>
          </label>
          <span class="required-hint">{{
            (currentFund.fundType === fundTypes.CHARITY
              ? 'distribution.form.required_field'
              : 'grant.form.required_field'
            ) | translateLang
          }}</span>
        </div>
        <div class="amount-input-container">
          <div class="amount-section">
            <input
              type="text"
              class="p-inputtext w-full"
              [class.p-invalid]="amount.invalid && (amount.dirty || amount.touched)"
              [value]="displayAmount"
              [disabled]="formDisabled"
              [placeholder]="'0' + decimalSymbol + '00'"
              (input)="onAmountInput($event)"
              (blur)="onAmountBlur($event)"
              (keydown)="onAmountKeyDown($event)"
            />
          </div>
          <div class="currency-section">
            <span class="currency-tag">{{
              displayCurrencySymbol || currentFund.currencySymbol
            }}</span>
          </div>
        </div>
        @if (apiCalculating) {
          <small class="checking-text">{{
            (currentFund.fundType === fundTypes.CHARITY
              ? 'distribution.form.checking_amount'
              : 'grant.form.checking_amount'
            ) | translateLang
          }}</small>
        }
        @if (amount.invalid && (amount.dirty || amount.touched) && !showError) {
          <small class="error-text">
            @if (amount.errors?.['required']) {
              {{
                (currentFund.fundType === fundTypes.CHARITY
                  ? 'distribution.form.amount.error_required'
                  : 'grant.form.amount.error_required'
                ) | translateLang
              }}
            } @else if (amount.errors?.['maxAmountAllowed']) {
              {{
                (currentFund.fundType === fundTypes.CHARITY
                  ? 'distribution.form.amount.error_max'
                  : 'grant.form.amount.error_max'
                ) | translateLang
              }}
            } @else if (amount.errors?.['noDecimals']) {
              {{
                (currentFund.fundType === fundTypes.CHARITY
                  ? 'distribution.form.amount.error_no_decimals'
                  : 'grant.form.amount.error_no_decimals'
                ) | translateLang
              }}
            }
          </small>
        }
        @if (showError && grantDataMinAmount) {
          <small class="error-text">
            {{
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.form.amount.error_min'
                : 'grant.form.amount.error_min'
              ) | translateLang
            }}:
            <strong>{{ formatNumberValue(grantDataMinAmount) }}</strong>
            {{ displayCurrencySymbol || currentFund.currencySymbol }}
          </small>
        } @else if (showError) {
          <small class="error-text">
            {{
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.error.server'
                : 'grant.error.server'
              ) | translateLang
            }}
          </small>
        }
        <p class="field-helper">
          {{
            (currentFund.fundType === fundTypes.CHARITY
              ? 'distribution.form.amount.available'
              : 'grant.form.amount.available'
            ) | translateLang
          }}
          <strong>{{ formatNumberValue(availableAmountToDisplay) }}</strong>
          {{ displayCurrencySymbol || currentFund.currencySymbol }}
        </p>
      </div>

      <!-- Timing -->
      <div class="field-group mt-4">
        <div class="field-label-row">
          <label class="field-label">
            {{
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.form.timing.label'
                : 'grant.form.timing.label'
              ) | translateLang
            }}<span class="required-asterisk">*</span>
          </label>
        </div>
        <div class="radio-group">
          <div class="radio-option">
            <p-radiobutton
              formControlName="timing"
              value="asap"
              name="timing"
              inputId="timing-asap"
            />
            <label for="timing-asap" class="radio-label">{{
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.form.timing.asap'
                : 'grant.form.timing.asap'
              ) | translateLang
            }}</label>
          </div>
          <div class="radio-option">
            <p-radiobutton
              formControlName="timing"
              value="specificDate"
              name="timing"
              inputId="timing-specific-date"
            />
            <label for="timing-specific-date" class="radio-label">{{
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.form.timing.specific_date'
                : 'grant.form.timing.specific_date'
              ) | translateLang
            }}</label>
          </div>
          @if (showRecurring) {
            <div class="radio-option">
              <p-radiobutton
                formControlName="timing"
                value="recurring"
                name="timing"
                inputId="timing-recurring"
              />
              <label for="timing-recurring" class="radio-label">{{
                (currentFund.fundType === fundTypes.CHARITY
                  ? 'distribution.form.recurring.label'
                  : 'grant.form.recurring.label'
                ) | translateLang
              }}</label>
            </div>
          }
        </div>
        @if (timing.invalid && (timing.dirty || timing.touched)) {
          <small class="error-text">{{ 'validation.required' | translateLang }}</small>
        }
      </div>

      <!-- Date Picker (shown only when specificDate is selected) -->
      @if (timing.value === 'specificDate') {
        <div class="field-group field-group--half mt-3">
          <label class="field-label" for="specific-date-input">
            {{
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.form.timing.date_label'
                : 'grant.form.timing.date_label'
              ) | translateLang
            }}<span class="required-asterisk">*</span>
          </label>
          <p-datepicker
            formControlName="date"
            [minDate]="charityStartDay"
            [dateFormat]="dateFormatPrimeNG"
            [placeholder]="dateFormatPlaceholder"
            [showIcon]="true"
            inputId="specific-date-input"
            styleClass="w-full"
          />
          @if (date.invalid && (date.dirty || date.touched)) {
            <small class="error-text">{{ 'validation.required' | translateLang }}</small>
          }
        </div>
      }

      <!-- Privacy (only for non-CHARITY, non-same-fund receiver) -->
      @if (!isReceiverEqualWithSenderFund() && currentFund.fundType !== fundTypes.CHARITY) {
        <div class="field-group mt-4">
          <label class="field-label">{{ 'grant.form.privacy.label' | translateLang }}</label>
          <div class="checkbox-option">
            <p-checkbox formControlName="privacy" [binary]="true" inputId="privacy-anonymous" />
            <label for="privacy-anonymous" class="checkbox-label">
              <small>{{ 'grant.form.privacy.anonymous' | translateLang }}</small>
            </label>
          </div>
        </div>
      }

      @if (isRecurring) {
        <div class="field-group mt-3">
          <app-recurring-form
            [control]="$any(recurrenceForm)"
            [isStripe]="false"
            [hideNoEndDate]="true"
            [checkGlobalConfig]="true"
          ></app-recurring-form>
        </div>
        @if (showBalanceWarning) {
          <div class="balance-warning mt-3">
            <app-icon name="exclamation-outline" [size]="20" color="#ffc609"></app-icon>
            <span>{{ 'manage_recurring_all.edit.balance_warning' | translateLang }}</span>
          </div>
        }
      }
    </div>

    <hr class="section-divider" />

    <!-- ================================================================ -->
    <!-- SECTION 3: INSTRUCTIONS (Optional) -->
    <!-- ================================================================ -->
    <div class="form-section">
      <div class="section-header">
        <span class="section-number">3</span>
        <span class="section-title">{{
          (currentFund.fundType === fundTypes.CHARITY
            ? 'distribution.form.instructions.title'
            : 'grant.form.instructions.title'
          ) | translateLang
        }}</span>
        <span class="section-optional">({{ 'general.optional' | translateLang }})</span>
      </div>

      <!-- Special Instructions for Charity (non-CHARITY fund type, receiver != sender) -->
      @if (!isReceiverEqualWithSenderFund() && currentFund.fundType !== fundTypes.CHARITY) {
        <div class="field-group">
          <div class="checkbox-option">
            <p-checkbox
              formControlName="showCharityInstruction"
              [binary]="true"
              inputId="show-charity-instruction"
            />
            <label for="show-charity-instruction" class="checkbox-label">
              {{ 'grant.form.instructions.charity_label' | translateLang }}
            </label>
          </div>
          @if (showCharityInstruction.value) {
            <textarea
              class="grant-textarea"
              formControlName="charityInstruction"
              [placeholder]="'grant.form.instructions.charity_placeholder' | translateLang"
            ></textarea>
            @if (
              charityInstruction.invalid && (charityInstruction.dirty || charityInstruction.touched)
            ) {
              <small class="error-text">
                @if (charityInstruction.errors?.['minLength']) {
                  {{ 'grant.form.instructions.error_min_length' | translateLang }}
                } @else if (charityInstruction.errors?.['maxLength']) {
                  {{ 'grant.form.instructions.error_max_length' | translateLang }}
                }
              </small>
            }
          }
        </div>
      }

      <!-- Additional Notes for Organization -->
      <div class="field-group">
        <div class="checkbox-option mb-2">
          <p-checkbox
            formControlName="showAdditionalNotes"
            [binary]="true"
            inputId="show-additional-notes"
          />
          <label for="show-additional-notes" class="checkbox-label">
            {{
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.form.instructions.org_label'
                : 'grant.form.instructions.org_label'
              ) | translateLang
            }}
            <strong>{{ instanceName }}</strong>
          </label>
        </div>
        @if (showAdditionalNotes.value) {
          <textarea
            class="grant-textarea"
            formControlName="additionalNotes"
            [placeholder]="
              (currentFund.fundType === fundTypes.CHARITY
                ? 'distribution.form.instructions.org_placeholder'
                : 'grant.form.instructions.org_placeholder'
              ) | translateLang
            "
          ></textarea>
          @if (additionalNotes.invalid && (additionalNotes.dirty || additionalNotes.touched)) {
            <small class="error-text">
              @if (additionalNotes.errors?.['minLength']) {
                {{ 'distribution.form.instructions.error_min_length' | translateLang }}
              } @else if (additionalNotes.errors?.['maxLength']) {
                {{ 'distribution.form.instructions.error_max_length' | translateLang }}
              }
            </small>
          }
        }
      </div>
    </div>
  </form>
}
